import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, Empty, Badge, Tabs } from "@/components/Kit";
import { useYearOptions } from "@/lib/academicYear";
import ProgressTemplate from "@/components/print/ProgressTemplate";
import { useTemplates, Scaled, PrintButton } from "@/components/print/PrintKit";
import { User, Users, Loader2 } from "lucide-react";

export default function ProgressRecordTab() {
  const [mode, setMode] = useState("single");
  return (
    <div data-testid="progress-tab">
      <Tabs testid="pr-mode" active={mode} onChange={setMode} tabs={[{ key: "single", label: "Single Student", icon: User }, { key: "bulk", label: "Whole Class (Bulk Print)", icon: Users }]} />
      {mode === "single" ? <Single /> : <Bulk />}
    </div>
  );
}

function Single() {
  const { school } = useAuth();
  const { data: tpl } = useTemplates();
  const { years: YEARS, current } = useYearOptions();
  const [studentId, setStudentId] = useState("");
  const [year, setYear] = useState("");
  const yr = year || current;
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/school/students")).data });
  const { data: rc } = useQuery({
    queryKey: ["report-card", studentId, yr], enabled: !!studentId && !!yr,
    queryFn: async () => (await api.get(`/school/academics/report-card/${studentId}`, { params: { academic_year: yr } })).data,
  });
  return (
    <>
      <div className="ep-card mb-4 grid gap-3 p-4 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
        <Select label="Student" value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="pr-student"><option value="">Choose student…</option>{students.map((s) => <option key={s.id} value={s.id}>{`${s.name} (${s.class_name}-${s.section}) · ${s.admission_no}`}</option>)}</Select>
        <Select label="Academic Year" value={yr} onChange={(e) => setYear(e.target.value)} data-testid="pr-year">{YEARS.map((y) => <option key={y}>{y}</option>)}</Select>
        {rc ? (
          <PrintButton title={`Progress Record ${rc.student.name}`} size="A4 portrait" label="Print A4 (Colour)" testid="pr-print-btn" className="ep-btn"><ProgressTemplate rc={rc} school={school} tpl={tpl} /></PrintButton>
        ) : <button className="ep-btn" disabled data-testid="pr-print-btn">Print A4 (Colour)</button>}
      </div>
      {!rc ? <div className="ep-card"><Empty>Select a student to compile their Progress Record from exam marks</Empty></div> : (
        <div className="ep-card p-5">
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
            <Badge color="#4F46E5">{rc.exams.length} exams</Badge><Badge color="#10B981">Total {rc.grand_total}/{rc.grand_max}</Badge><Badge color="#F59E0B">{rc.percentage}% · Grade {rc.grade}</Badge><Badge color="#06B6D4">Attendance {rc.attendance.present}/{rc.attendance.working_days}</Badge>
          </div>
          <div className="flex justify-center overflow-auto rounded-xl bg-slate-300 p-4" data-testid="progress-record">
            <Scaled scale={0.9} width="8.27in" height="11.69in"><ProgressTemplate rc={rc} school={school} tpl={tpl} /></Scaled>
          </div>
        </div>
      )}
    </>
  );
}

function Bulk() {
  const { school } = useAuth();
  const { data: tpl } = useTemplates();
  const { years: YEARS, current } = useYearOptions();
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("A");
  const [year, setYear] = useState("");
  const yr = year || current;
  const { data: classes = [] } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/school/academics/classes")).data });
  const sections = classes.find((c) => c.name === cls)?.sections || ["A"];
  const { data: list, isFetching } = useQuery({
    queryKey: ["report-cards", cls, sec, yr], enabled: !!cls && !!yr,
    queryFn: async () => (await api.get("/school/academics/report-cards", { params: { class_name: cls, section: sec, academic_year: yr } })).data,
  });
  const withMarks = (list || []).filter((r) => r.rows.length);
  return (
    <>
      <div className="ep-card mb-4 grid gap-3 p-4 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
        <Select label="Class" value={cls} onChange={(e) => { setCls(e.target.value); setSec("A"); }} data-testid="pb-class"><option value="">Select</option>{classes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</Select>
        <Select label="Section" value={sec} onChange={(e) => setSec(e.target.value)} data-testid="pb-section">{sections.map((s) => <option key={s}>{s}</option>)}</Select>
        <Select label="Academic Year" value={yr} onChange={(e) => setYear(e.target.value)} data-testid="pb-year">{YEARS.map((y) => <option key={y}>{y}</option>)}</Select>
        {withMarks.length ? (
          <PrintButton title={`Progress Records ${cls}-${sec}`} size="A4 portrait" label={`Print ${withMarks.length} Records (A4)`} testid="pb-print-btn" className="ep-btn">
            {withMarks.map((rc) => <ProgressTemplate key={rc.student.id} rc={rc} school={school} tpl={tpl} />)}
          </PrintButton>
        ) : <button className="ep-btn" disabled data-testid="pb-print-btn">{isFetching ? <Loader2 size={15} className="animate-spin" /> : null} Print Records (A4)</button>}
      </div>
      {!cls ? <div className="ep-card"><Empty>Select a class & section to compile progress records for every student</Empty></div> : !list ? <div className="ep-card"><Empty>Compiling…</Empty></div> : (
        <div className="ep-card overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 px-4 py-3 text-sm text-slate-300">
            <b className="text-white">Class {cls}-{sec}</b> · {list.length} students · <span className="text-emerald-400">{withMarks.length} with marks</span> · <span className="text-slate-500">{list.length - withMarks.length} without marks (skipped)</span>
          </div>
          <table className="w-full text-sm" data-testid="pb-table">
            <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-2.5">#</th><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Adm No</th><th className="px-4 py-2.5 text-right">Total</th><th className="px-4 py-2.5 text-right">%</th><th className="px-4 py-2.5 text-center">Grade</th><th className="px-4 py-2.5 text-center">Attendance</th></tr></thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.student.id} className={`border-b border-slate-800/50 ${r.rows.length ? "" : "opacity-50"}`} data-testid={`pb-row-${r.student.admission_no}`}>
                  <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-white">{r.student.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-400">{r.student.admission_no}</td>
                  <td className="px-4 py-2 text-right text-slate-300">{r.rows.length ? `${r.grand_total}/${r.grand_max}` : "—"}</td>
                  <td className="px-4 py-2 text-right text-slate-300">{r.rows.length ? `${r.percentage}%` : "—"}</td>
                  <td className="px-4 py-2 text-center"><Badge color={r.rows.length ? "#4F46E5" : "#64748B"}>{r.grade}</Badge></td>
                  <td className="px-4 py-2 text-center text-slate-400">{r.attendance.present}/{r.attendance.working_days}</td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={7}><Empty>No students in this class-section</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
